import React, { useMemo, useState } from 'react';
import EventActionsMenu from './EventActionsMenu';
import { EventStatusPill } from './EventCard';
import { dayKey, eventTimeRange, eventTypeLabel, formatEventDate, monthWeeks, parseEventDay } from './eventConstants';
import { pillClass, TILE_CLASS } from './eventStyles';
import { DEFAULT_EVENT_WORDS, countWord } from './eventWords';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Month view. On a wide screen each day shows its event names; on a phone each day shows a count and tapping a day lists
// that day's events underneath, so nothing is squeezed into a cell that cannot hold it.
export default function EventsCalendarView({ events, kits, canEdit, onEdit, onCancel, onGuests, onPlan, today = new Date(), words = DEFAULT_EVENT_WORDS }) {
    const firstEvent = useMemo(() => events.find((event) => parseEventDay(event.event_date)), [events]);
    const startDay = firstEvent ? parseEventDay(firstEvent.event_date) : today;
    const [cursor, setCursor] = useState({ year: startDay.getFullYear(), month: startDay.getMonth() });
    const [selected, setSelected] = useState(() => dayKey(startDay));

    const byDay = useMemo(() => {
        const map = new Map();
        events.forEach((event) => {
            const key = String(event.event_date || '').slice(0, 10);
            if (!key) return;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(event);
        });
        return map;
    }, [events]);

    const weeks = useMemo(() => monthWeeks(cursor.year, cursor.month), [cursor]);
    const monthTitle = new Date(cursor.year, cursor.month, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const todayKey = dayKey(today);
    const selectedEvents = byDay.get(selected) || [];

    const move = (delta) => setCursor(({ year, month }) => {
        const next = new Date(year, month + delta, 1);
        return { year: next.getFullYear(), month: next.getMonth() };
    });
    const goToday = () => {
        setCursor({ year: today.getFullYear(), month: today.getMonth() });
        setSelected(todayKey);
    };

    return (
        <div data-testid="events-calendar" className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-lg font-semibold text-[var(--ink)]" aria-live="polite">{monthTitle}</h3>
                <div className="flex items-center gap-2" role="group" aria-label="Change month">
                    <button type="button" className={pillClass(false)} onClick={() => move(-1)} aria-label="Previous month">‹</button>
                    <button type="button" className={pillClass(false)} onClick={goToday}>Today</button>
                    <button type="button" className={pillClass(false)} onClick={() => move(1)} aria-label="Next month">›</button>
                </div>
            </div>

            <div className={`${TILE_CLASS} ia-tile3d--clip pt-[5px]`}>
                <div className="grid grid-cols-7 border-b border-[var(--surface-border)] bg-[var(--surface-sunken)]">
                    {WEEKDAYS.map((name) => (
                        <div key={name} className="py-2 text-center text-[11px] sm:text-xs font-semibold uppercase tracking-wide text-[var(--ink-muted)]">{name}</div>
                    ))}
                </div>
                {weeks.map((week) => (
                    <div key={dayKey(week[0])} className="grid grid-cols-7">
                        {week.map((date) => {
                            const key = dayKey(date);
                            const inMonth = date.getMonth() === cursor.month;
                            const dayEvents = byDay.get(key) || [];
                            const isSelected = key === selected;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setSelected(key)}
                                    aria-pressed={isSelected}
                                    aria-label={`${formatEventDate(key, { weekday: 'long', month: 'long', day: 'numeric' })}, ${countWord(dayEvents.length, words)}`}
                                    data-testid="calendar-day"
                                    className={`min-h-[52px] sm:min-h-[92px] min-w-0 border-b border-r border-[var(--surface-border)] p-1 text-left align-top flex flex-col gap-0.5 transition ${inMonth ? '' : 'opacity-40'} ${isSelected ? 'ring-2 ring-inset ring-[var(--selected-pill-border)]' : 'hover:bg-[var(--surface-sunken)]'}`}
                                >
                                    <span className={`text-xs sm:text-sm font-medium ${key === todayKey ? 'text-[var(--selected-pill-ink)] font-bold' : 'text-[var(--ink)]'}`}>{date.getDate()}</span>
                                    {dayEvents.length > 0 && (
                                        <>
                                            <span className="sm:hidden self-start rounded-full border border-[var(--selected-pill-border)] px-1.5 text-[10px] leading-4 text-[var(--selected-pill-ink)]">{dayEvents.length}</span>
                                            <span className="hidden sm:flex flex-col gap-0.5 min-w-0">
                                                {dayEvents.slice(0, 2).map((event) => (
                                                    <span key={event.id} className={`truncate rounded border border-[var(--selected-pill-border)] px-1 text-xs text-[var(--ink)] ${event.engagement?.status === 'canceled' ? 'line-through opacity-70' : ''}`}>{event.event_name}</span>
                                                ))}
                                                {dayEvents.length > 2 && <span className="text-xs text-[var(--ink-muted)]">+{dayEvents.length - 2} more</span>}
                                            </span>
                                        </>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                ))}
            </div>

            <section aria-label={`${words.events} on the selected day`} className={`${TILE_CLASS} p-3 pt-4`}>
                <h4 className="mb-2 text-sm font-semibold text-[var(--ink)]">{formatEventDate(selected, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</h4>
                {selectedEvents.length === 0 ? (
                    <p className="text-sm text-[var(--ink-muted)]">No {words.eventsLower} this day.</p>
                ) : (
                    <ul className="space-y-2">
                        {selectedEvents.map((event) => (
                            <li key={event.id} data-testid="calendar-agenda-item" className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[var(--surface-border)] p-2 min-w-0">
                                <div className="min-w-0">
                                    <div className={`font-medium text-[var(--ink)] break-words ${event.engagement?.status === 'canceled' ? 'line-through' : ''}`}>{event.event_name}</div>
                                    <div className="text-sm text-[var(--ink-muted)]">{eventTimeRange(event)} · {eventTypeLabel(kits, event.event_type)}{event.venue_name ? ` · ${event.venue_name}` : ''}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <EventStatusPill status={event.engagement?.status} />
                                    <EventActionsMenu words={words} event={event} canEdit={canEdit} onEdit={onEdit} onCancel={onCancel} onGuests={onGuests} onPlan={onPlan} />
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}
